import { useEffect, useState } from "react";
import { api, apiError, eur, itDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Empty, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileDown, Mail, CircleDollarSign } from "lucide-react";

export default function Fatture() {
  const [rows, setRows] = useState([]);
  const [onlyUnpaid, setOnlyUnpaid] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/invoices").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const shown = onlyUnpaid ? rows.filter((r) => r.status !== "incassato" && r.status !== "annullato") : rows;
  const totalUnpaid = rows.filter((r) => r.status === "fatturato").reduce((s, r) => s + (r.amount || 0), 0);

  const ENDPOINTS = {
    intervento: "interventions", contratto: "contracts", vendita: "direct-sales", iscrizione: "enrollments",
  };
  const endpoint = (r) => ENDPOINTS[r.source] || "interventions";

  const downloadPdf = async (r) => {
    try {
      const { data } = await api.get(`/${endpoint(r)}/${r.source_id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg("Download della proforma non riuscito.");
    }
  };

  const sendInvoice = async (r) => {
    try {
      const { data } = await api.post(`/${endpoint(r)}/${r.source_id}/send-invoice`);
      setMsg(`Proforma ${r.code} inviata a ${data.sent_to}.`);
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };

  const markPaid = async (r) => {
    try {
      await api.post(`/${endpoint(r)}/${r.source_id}/paid`);
      setMsg(`${r.code} segnata come incassata.`);
      await load();
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="fatture-page">
      <PageHeader title="Fatture" subtitle={`${shown.length} proforma · ${eur(totalUnpaid)} da incassare`}>
        <Button
          variant={onlyUnpaid ? "default" : "outline"}
          className="h-11 rounded-sm"
          onClick={() => setOnlyUnpaid((v) => !v)}
          data-testid="toggle-unpaid-filter"
        >
          {onlyUnpaid ? "Solo non incassate ✓" : "Solo non incassate"}
        </Button>
      </PageHeader>

      {msg && <p className="mb-4 text-sm font-medium text-primary" data-testid="fatture-message">{msg}</p>}

      <ListCard testid="fatture-list">
        {shown.map((r) => (
          <div key={`${r.source}-${r.source_id}`} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-muted-foreground">{r.code}</span>
                <p className="font-semibold">{r.customer_name || "—"}</p>
                <Badge
                  variant={r.status === "incassato" ? "secondary" : r.status === "annullato" ? "destructive" : "default"}
                  className="rounded-sm capitalize"
                >
                  {r.status}
                </Badge>
                <Badge variant="outline" className="rounded-sm capitalize">{r.source}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {r.description} · emessa il {itDate(r.invoiced_at)}
                {r.paid_at ? ` · incassata il ${itDate(r.paid_at)}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-mono text-sm font-semibold">{eur(r.amount)}</span>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => downloadPdf(r)}
                      data-testid={`invoice-pdf-${r.source}-${r.source_id}`}>
                <FileDown className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => sendInvoice(r)}
                      data-testid={`send-invoice-${r.source}-${r.source_id}`}>
                <Mail className="h-3.5 w-3.5" />
              </Button>
              {r.status === "fatturato" && (
                <Button size="sm" className="rounded-sm" onClick={() => markPaid(r)}
                        data-testid={`mark-paid-${r.source}-${r.source_id}`}>
                  <CircleDollarSign className="h-3.5 w-3.5 mr-2" /> Incassato
                </Button>
              )}
            </div>
          </div>
        ))}
        {shown.length === 0 && <Empty text="Nessuna fattura emessa." />}
      </ListCard>
    </div>
  );
}
